import { TextInput, useDialogState } from '@tricor-digital-team/vistra-design-system';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { ArrowRightIcon, PrimaryButton, TertiaryButton } from '@tricor-digital-team/vistra-design-system';
import { useSelector, useDispatch } from 'react-redux';
import { resetFormData, updateFormData } from '../../../../Redux/formSlice';
import { countries } from '../../../../Utils/Constants';
import CancelStep from '../CancelStep';
import { useNavigate } from 'react-router-dom';
import { resetIndividual } from '../../../../Redux/individualSlice';
import CustomSelect from '../../../../Components/CustomSelect';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  vistraContactName: z.string().optional(),
  vistraContactEmail: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, { message: 'Invalid email address' }),
  companyName: z.string().trim().min(1, 'Company name is required'),
  addressLine1: z.string().trim().min(1, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  addressLine3: z.string().optional(),
  town: z.string().trim().min(1, 'Town is required'),
  county: z.string().optional(),
  country: z.string().trim().min(1, 'Country is required'),
  // postcode: z.string().trim().min(1, 'Postcode is required'),
  postcode: z.string().optional()
});

const RequestorDetail = ({ setCurrentStep }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cancelDialog = useDialogState({ visible: false });
  const storedFormData = useSelector((state) => state.form.formData);
  const [formData, setFormData] = useState(storedFormData);
  const [resetCount, setResetCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setFormData(storedFormData); // Load Redux state on mount
  }, [storedFormData]);

  const handleChange = (value, name) => {
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    dispatch(updateFormData({ [name]: value }));

    // Validate the field
    const fieldValidation = schema.shape[name]?.safeParse(value);
    if ((name === 'email' || name === 'vistraContactEmail') && !fieldValidation?.success) {
      return;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: fieldValidation?.success ? '' : fieldValidation?.error?.format()?._errors?.[0] || 'Invalid input',
    }));
    if (isError) {
      const result = schema.safeParse(updatedFormData);
      if (result.success) {
        setIsError(false);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = schema.safeParse(formData);
    if (!result.success) {
      const errorMessages = result.error.format();
      setErrors(Object.fromEntries(Object.entries(errorMessages).map(([key, val]) => [key, val?._errors?.[0] || ''])));
      setIsError(true);
    } else {
      setErrors({});
      setIsError(false);
      setCurrentStep(1);
      console.log('Form submitted', formData);
    }
  };

  return (
    <div>
      <div className="text-[#68696C] text-sm">All fields are mandatory unless labeled as optional.</div>
      <div className="text-black text-2xl mt-6 font-normal">Customer information</div>
      <div className="text-black text-xl mt-6 font-normal">Customer contact</div>
      <div className="mt-4">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-1/2">
            <TextInput
              key={`name${resetCount}`}
              id="name"
              isRequired
              labelText="Name"
              name="name"
              placeholder="Enter full name"
              defaultValue={formData.name}
              errorText={errors.name}
              isInvalid={errors.name}
              onChange={(e) => handleChange(e.target.value, 'name')}
            />
          </div>
          <div className="w-full md:w-1/2">
            <TextInput
              key={`name${resetCount}`}
              id="email"
              isRequired
              labelText="Email"
              name="email"
              placeholder="e.g., abc@email.com"
              defaultValue={formData.email}
              errorText={errors.email}
              isInvalid={errors.email}
              onChange={(e) => handleChange(e.target.value, 'email')}
            />
          </div>
        </div>
      </div>
      <div className="mt-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-1/2">
            <TextInput
              key={`name${resetCount}`}
              id="jobTitle"
              isRequired
              labelText="Job title (optional)"
              name="jobTitle"
              placeholder=""
              defaultValue={formData.jobTitle}
              onChange={(e) => handleChange(e.target.value, 'jobTitle')}
            />
          </div>
          <div className="w-full md:w-1/2"></div>
        </div>
      </div>
      <div className="text-black text-xl mt-6 font-normal">Vistra contact person details (if applicable)</div>
      <div className="mt-4">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-1/2">
            <TextInput
              key={`name${resetCount}`}
              id="vistraContactName"
              isRequired
              labelText="Name"
              name="vistraContactName"
              placeholder="Enter contract name"
              defaultValue={formData.vistraContactName}
              errorText={errors.vistraContactName}
              isInvalid={errors.vistraContactName}
              onChange={(e) => handleChange(e.target.value, 'vistraContactName')}
            />
          </div>
          <div className="w-full md:w-1/2">
            <TextInput
              key={`name${resetCount}`}
              id="vistraContactEmail"
              isRequired
              labelText="Email"
              name="vistraContactEmail"
              placeholder="e.g., abc@email.com"
              defaultValue={formData.vistraContactEmail}
              errorText={errors.vistraContactEmail}
              isInvalid={errors.vistraContactEmail}
              onChange={(e) => handleChange(e.target.value, 'vistraContactEmail')}
            />
          </div>
        </div>
      </div>
      <div className="text-black text-2xl mt-12 font-normal">Customer address</div>
      <div className="w-full mt-6">
        <TextInput
          key={`name${resetCount}`}
          isRequired
          labelText="Company name"
          name="companyName"
          id="companyName"
          defaultValue={formData.companyName}
          errorText={errors.companyName}
          isInvalid={errors.companyName}
          onChange={(e) => handleChange(e.target.value, 'companyName')}
        />
      </div>
      <div className="w-full mt-6">
        <TextInput
          key={`name${resetCount}`}
          isRequired
          labelText="Address line 1"
          name="addressLine1"
          id="addressLine1"
          defaultValue={formData.addressLine1}
          errorText={errors.addressLine1}
          isInvalid={errors.addressLine1}
          onChange={(e) => handleChange(e.target.value, 'addressLine1')}
        />
      </div>
      <div className="w-full mt-6">
        <TextInput
          key={`name${resetCount}`}
          isRequired
          labelText="Address line 2 (optional)"
          name="addressLine2"
          id="addressLine2"
          defaultValue={formData.addressLine2}
          errorText={errors.addressLine2}
          isInvalid={errors.addressLine2}
          onChange={(e) => handleChange(e.target.value, 'addressLine2')}
        />
      </div>
      <div className="w-full mt-6">
        <TextInput
          key={`name${resetCount}`}
          isRequired
          labelText="Address line 3 (optional)"
          name="addressLine3"
          id="addressLine3"
          defaultValue={formData.addressLine3}
          errorText={errors.addressLine3}
          isInvalid={errors.addressLine3}
          onChange={(e) => handleChange(e.target.value, 'addressLine3')}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="w-full md:w-1/2">
          <TextInput
            key={`name${resetCount}`}
            isRequired
            labelText="Town"
            name="town"
            id="town"
            defaultValue={formData.town}
            errorText={errors.town}
            isInvalid={errors.town}
            onChange={(e) => handleChange(e.target.value, 'town')}
          />
        </div>
        <div className="w-full md:w-1/2">
          <TextInput
            key={`name${resetCount}`}
            isRequired
            labelText="County (optional)"
            name="county"
            id="county"
            defaultValue={formData.county}
            errorText={errors.county}
            isInvalid={errors.county}
            onChange={(e) => handleChange(e.target.value, 'county')}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="w-full md:w-1/2">
          <CustomSelect
            labelText="Country"
            isRequired
            isInvalid={!!errors.country}
            errorText={errors.country}
            value={countries.find((country) => country.value === formData?.country) || null}
            options={countries}
            id="country"
            placeholder={formData?.country ? formData?.country : 'Enter country'}
            key={`name${resetCount}`}
            name="country"
            onChange={(value) => {
              if (value) {
                handleChange(value, 'country');
              }
            }}
          />
        </div>
        <div className="w-full md:w-1/2">
          <TextInput
            key={`name${resetCount}`}
            isRequired
            labelText="Postcode (optional)"
            name="postcode"
            id="postcode"
            defaultValue={formData.postcode}
            errorText={errors.postcode}
            isInvalid={errors.postcode}
            onChange={(e) => handleChange(e.target.value, 'postcode')}
          />
        </div>
      </div>
      <div className="flex justify-between items-center mt-12">
        <TertiaryButton
          label="Cancel"
          onClick={() => {
            cancelDialog.show();
          }}
          size="large"
        />
        <PrimaryButton
          icon={ArrowRightIcon}
          disabled={isError}
          iconPosition="right"
          label="Next"
          onClick={(e) => {
            handleSubmit(e);
            setResetCount((prev) => prev + 1);
          }}
          size="large"
        />
      </div>
      <CancelStep
        dialog={cancelDialog}
        handleExit={() => {
          dispatch(resetFormData());
          dispatch(resetIndividual());
          cancelDialog.hide();
          navigate('/');
        }}
      />
    </div>
  );
};

export default RequestorDetail;
